"use client";

import { clsx } from "clsx";
import * as SelectPrimitive from "@radix-ui/react-select";

export const SELECT_NONE_VALUE = "__none__";

type SelectItem<V extends string = string> = {
  value: V;
  label: string;
  disabled?: boolean;
  comingSoon?: boolean;
};

type SelectProps<V extends string> = {
  value: V;
  onChange: (next: V) => void;
  items: SelectItem<V>[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
} & (
  | { ariaLabel: string; ariaLabelledby?: never }
  | { ariaLabel?: never; ariaLabelledby: string }
);

export function Select<V extends string>({
  value,
  onChange,
  items,
  ariaLabel,
  ariaLabelledby,
  placeholder,
  disabled,
  className,
}: SelectProps<V>) {
  return (
    <SelectPrimitive.Root
      value={value}
      onValueChange={onChange as (v: string) => void}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        className={clsx(
          "group inline-flex items-center gap-[8px] h-10 pl-[14px] pr-3 rounded-full border",
          "text-sm font-semibold shadow-[var(--shadow-sm)] cursor-pointer",
          "transition-[border-color,background] duration-[180ms]",
          "focus-ring-tight",
          "disabled:opacity-70 disabled:cursor-not-allowed",
          "data-[state=closed]:border-border-strong data-[state=closed]:bg-surface-muted data-[state=closed]:text-text",
          "data-[state=closed]:hover:border-border-strong",
          "data-[state=open]:border-primary data-[state=open]:bg-canopy-50 data-[state=open]:text-primary",
          "data-[placeholder]:text-text-muted",
          className,
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <svg
            className="w-[14px] h-[14px] flex-shrink-0 transition-transform duration-[180ms] text-text-muted group-data-[state=open]:rotate-180 group-data-[state=open]:text-primary"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          align="start"
          sideOffset={8}
          className={clsx(
            "z-50 min-w-[var(--radix-select-trigger-width)] max-h-80",
            "overflow-y-auto rounded-[var(--radius-md)] border border-border bg-surface-muted shadow-[var(--shadow-md)] p-1",
          )}
        >
          <SelectPrimitive.Viewport>
            {items.map((item) => (
              <SelectPrimitive.Item
                key={item.value}
                value={item.value}
                disabled={item.disabled || item.comingSoon}
                className={clsx(
                  "flex items-center gap-3 px-3 py-[10px] rounded-[var(--radius-sm)] text-sm text-text",
                  "cursor-pointer outline-none select-none",
                  "transition-colors duration-[120ms]",
                  "data-[highlighted]:bg-canopy-50 data-[highlighted]:text-primary",
                  "data-[state=checked]:font-semibold data-[state=checked]:text-primary",
                  "data-[disabled]:text-text-muted data-[disabled]:cursor-not-allowed",
                )}
              >
                <SelectPrimitive.ItemText>
                  {item.comingSoon ? `${item.label} (Coming soon)` : item.label}
                </SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="ml-auto flex-shrink-0">
                  <svg
                    className="w-4 h-4 text-primary"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
